import { c as _c } from "react/compiler-runtime";
import { useRequestErrorToast } from '@onefootprint/hooks';
import request from '@onefootprint/request';
import type { AssumeRoleResponse } from '@onefootprint/request-types/dashboard';
import type { CopyPlaybookRequest, CopyPlaybookResponse, OnboardingConfig } from '@onefootprint/types';
import { useToast } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import useSession, { type AuthHeaders } from '@/hooks/use-session';
const copyPlaybook = async (authHeaders: AuthHeaders, {
  playbookId,
  name,
  tenantId
}: CopyPlaybookRequest) => {
  const assumeResponse = await request<AssumeRoleResponse>({
    method: 'POST',
    url: '/org/auth/assume_role',
    headers: authHeaders,
    data: {
      tenantId
    }
  });
  const copyResponse = await request<CopyPlaybookResponse>({
    method: 'POST',
    url: `/org/onboarding_configs/${playbookId}/copy`,
    headers: {
      ...authHeaders,
      'x-fp-dashboard-authorization-secondary': assumeResponse.data.token
    },
    data: {
      name
    }
  });
  return copyResponse.data;
};
const useCopyPlaybook = () => {
  const $ = _c(17);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "copy.form.feedback"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("playbooks", t0);
  const queryClient = useQueryClient();
  const showErrorToast = useRequestErrorToast();
  const {
    toast
  } = useToast();
  const router = useRouter();
  const {
    authHeaders,
    isLive,
    setIsLive,
    data
  } = useSession();
  let t1;
  if ($[1] !== data || $[2] !== isLive || $[3] !== router || $[4] !== setIsLive) {
    t1 = async (playbook, payload) => {
      const needsToAssumeTenant = payload.tenantId !== data.org?.id;
      const needsToSwitchMode = playbook.isLive !== isLive;
      if (needsToAssumeTenant && data.auth) {
        router.push({
          pathname: "/switch-org",
          query: {
            tenant_id: payload.tenantId,
            mode: playbook.isLive ? "live" : "sandbox",
            redirect_url: `/playbooks/${playbook.playbookId}`
          }
        });
        return;
      }
      if (needsToSwitchMode) {
        await setIsLive(playbook.isLive);
      }
      router.push({
        pathname: `/playbooks/${playbook.playbookId}`
      });
    };
    $[1] = data;
    $[2] = isLive;
    $[3] = router;
    $[4] = setIsLive;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleCtaClick = t1;
  let t2;
  if ($[6] !== authHeaders) {
    t2 = payload_0 => copyPlaybook(authHeaders, payload_0);
    $[6] = authHeaders;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== handleCtaClick || $[9] !== queryClient || $[10] !== t || $[11] !== toast) {
    t3 = (response, payload_1) => {
      toast({
        description: t(_temp, {
          orgName: payload_1.tenantName
        }),
        title: t(_temp2),
        primaryAction: {
          label: t(_temp3),
          onClick: () => handleCtaClick(response, payload_1)
        }
      });
      queryClient.invalidateQueries();
    };
    $[8] = handleCtaClick;
    $[9] = queryClient;
    $[10] = t;
    $[11] = toast;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== showErrorToast || $[14] !== t2 || $[15] !== t3) {
    t4 = {
      mutationFn: t2,
      onSuccess: t3,
      onError: showErrorToast
    };
    $[13] = showErrorToast;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return useMutation(t4);
};
export default useCopyPlaybook;
function _temp($) {
  return $.success.description;
}
function _temp2($_0) {
  return $_0.success.title;
}
function _temp3($_1) {
  return $_1.success.cta;
}